import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';

const DeleteEvent = graphql(/* GraphQL */ `
  mutation DeleteEvent($eventId: uuid!) {
    delete_event_by_pk(id: $eventId) {
      id
    }
  }
`);

const GetDeleteContext = graphql(`
  query GetEventUpdateDeleteContext($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      title
      event_type
    }
  }
`);

export default function EventUpdateDelete() {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const { dismissAll, back } = useRouter();
  const [, remove] = useMutation(DeleteEvent);
  const [result, refresh] = useQuery({
    query: GetDeleteContext,
    variables: { eventId },
  });

  const entity = result.data?.event_by_pk;

  const removeEntity = async () => {
    const result = await remove({ eventId });
    if (result.error || !result.data?.delete_event_by_pk)
      return { success: false };
    return { success: true };
  };

  return (
    <>
      <Stack.Screen
        options={{ title: entity?.title ? `Delete ${entity.title}` : 'Delete' }}
      />
      <ContentBoundary
        data={entity}
        loading={result.fetching}
        error={result.error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Deletion is unavailable." />}
      >
        {(entity) => (
          <DestructiveAction
            key={entity.id}
            confirmationName={
              entity.event_type === 'group'
                ? (entity.title ?? undefined)
                : undefined
            }
            label={
              entity.event_type === 'group' ? 'Delete schedule' : 'Delete event'
            }
            submittingLabel="Deleting…"
            onAction={removeEntity}
            onSuccess={() => {
              dismissAll();
              back();
              back();
            }}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete {entity.title || 'this event'}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
